import React, { useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import PageLiveDocIcon from '@atlaskit/icon-lab/core/page-live-doc';

import {
	WHITEBOARD_TEMPLATE_IDS,
	WHITEBOARD_TEMPLATE_TITLE_MAPPING,
} from '../localMappings/whiteboardMappings.ts';
import {
	BLUEPRINT_IMG_MAP_DARK,
	BLUEPRINT_NAME_TO_IMG_MAPPING,
	WHITEBOARD_TEMPLATE_TO_IMG_MAPPING,
	WHITEBOARD_TEMPLATE_TO_DARK_IMG_MAPPING,
	defaultImgSrc,
	defaultDarkImgSrc,
} from '../localMappings/templateToImgMappings.ts';
import { DEFAULT_POPULAR_TEMPLATES } from '../localMappings/defaultPopularTemplates.ts';
import {
	ConfluencePageSubType,
	buildCreationLink,
} from '../createContent/createContentRouteUtils.ts';
import { teamToTemplatesMapping } from '../localMappings/teamToTemplatesMapping.ts';
import { BLUEPRINT_NAME_TO_IDS_MAPPING } from '../localMappings/blueprintNameMapping.ts';
import type { BLUEPRINT_NAMES } from '../localMappings/blueprintNameMapping.ts';
import { messages } from '../components/TemplateModal.i18n.ts';
import type { BlueprintModuleKeyToLocalizedName } from './useAllTemplatesQuery.tsx';

// Create a Set for O(1) lookup performance
const WHITEBOARD_TEMPLATE_IDS_SET = new Set(Object.values(WHITEBOARD_TEMPLATE_IDS));

export interface TemplateData {
	name: string;
	contentType: 'whiteboard' | 'page';
	contentSubtype?: ConfluencePageSubType;
	contentTypeLabel?: string;
	contentTypeIcon?: React.ReactElement;
	imageSource: string;
	imageSourceDark: string;
	createLink: string;
}

export interface UseTemplateDataTransformResult {
	templates: TemplateData[];
	blankDocCreateLink: string;
}

/**
 * Hook that transforms team type into an array of template data objects
 * @param teamType - The team type to get templates for
 * @param spaceKey - Optional space key for creation links
 * @param parentPageId - Optional parent page ID for creation links
 * @param blueprintModuleKeyToLocalizedName - Optional lookup map from blueprintModuleCompleteKey to server-localized template name.
 *   When provided, blueprint template cards will display translated names from the server instead of hardcoded English strings.
 * @returns Object containing array of template data objects and a blank doc creation link
 */
export const useTemplateDataTransform = (
	teamType?: string,
	spaceKey?: string,
	parentPageId?: string,
	blueprintModuleKeyToLocalizedName?: BlueprintModuleKeyToLocalizedName,
): UseTemplateDataTransformResult => {
	const { formatMessage } = useIntl();

	const blankDocCreateLink = buildCreationLink({
		contentType: 'page',
		subType: ConfluencePageSubType.LIVE,
		withFallback: true,
		source: 'cfind-5291-template-touchpoint',
		spaceKey,
		parentPageId,
	});

	const templates = useMemo(() => {
		// Get the template names for this team type
		let templateNames = teamType ? teamToTemplatesMapping[teamType] : undefined;
		if (!templateNames || templateNames.length === 0) {
			templateNames = DEFAULT_POPULAR_TEMPLATES;
		}

		// Transform each template name into a TemplateData object using reduce to avoid nested iteration
		const templates: TemplateData[] = [];
		for (const templateName of templateNames) {
			if (WHITEBOARD_TEMPLATE_IDS_SET.has(templateName as WHITEBOARD_TEMPLATE_IDS)) {
				const titleDescriptor =
					WHITEBOARD_TEMPLATE_TITLE_MAPPING[templateName as WHITEBOARD_TEMPLATE_IDS];
				const imageSource =
					WHITEBOARD_TEMPLATE_TO_IMG_MAPPING[templateName as WHITEBOARD_TEMPLATE_IDS] ||
					defaultImgSrc;
				const imageSourceDark =
					WHITEBOARD_TEMPLATE_TO_DARK_IMG_MAPPING[templateName as WHITEBOARD_TEMPLATE_IDS] ||
					defaultDarkImgSrc;

				const createLink = buildCreationLink({
					contentType: 'whiteboard',
					withFallback: true,
					source: 'cfind-5291-template-touchpoint',
					spaceKey,
					parentPageId,
					insertTemplateWithKey: templateName,
				});

				templates.push({
					name: titleDescriptor ? formatMessage(titleDescriptor) : templateName,
					contentType: 'whiteboard',
					contentTypeLabel: formatMessage(messages.whiteboardTemplateType),
					contentTypeIcon: (
						<WhiteboardIcon label={formatMessage(messages.whiteboardTemplateType)} />
					),
					imageSource,
					imageSourceDark,
					createLink,
				});
				continue;
			}

			// Blueprint/Doc template - use extended mappings (includes all blueprints)
			const extendedBlueprintMapping =
				BLUEPRINT_NAME_TO_IDS_MAPPING[templateName as BLUEPRINT_NAMES];
			if (extendedBlueprintMapping) {
				const imageSource =
					BLUEPRINT_NAME_TO_IMG_MAPPING[templateName as BLUEPRINT_NAMES] || defaultImgSrc;
				const imageSourceDark =
					BLUEPRINT_IMG_MAP_DARK[templateName as BLUEPRINT_NAMES] || defaultDarkImgSrc;

				const createLink = buildCreationLink({
					contentType: 'page',
					subType: ConfluencePageSubType.LIVE,
					withFallback: true,
					source: 'cfind-5291-template-touchpoint',
					spaceKey,
					parentPageId,
					templateKey: extendedBlueprintMapping.blueprintModuleCompleteKey,
				});

				// Use the server-localized name if available, falling back to the hardcoded enum value
				const localizedName =
					blueprintModuleKeyToLocalizedName?.[
						extendedBlueprintMapping.blueprintModuleCompleteKey
					] ?? templateName;

				templates.push({
					name: localizedName,
					contentType: 'page',
					contentSubtype: ConfluencePageSubType.LIVE,
					contentTypeLabel: formatMessage(messages.livePageTemplateType),
					contentTypeIcon: <PageLiveDocIcon label="" />,
					imageSource,
					imageSourceDark,
					createLink,
				});
				continue;
			}
		}

		return templates;
	}, [teamType, formatMessage, spaceKey, parentPageId, blueprintModuleKeyToLocalizedName]);

	return { templates, blankDocCreateLink };
};
